import { Box, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { CloseIcon, colors } from '@silbo/ui';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import useLiveQuery from '../../../hooks/useLiveQuery';
import CleaningMissionDetails from '../components/CleaningMissionDetails';
import CleaningMissionGestures from '../components/CleaningMissionGestures';
import CleaningMissionPrioritySquare from '../components/CleaningMissionPrioritySquare';
import { useNow } from '../hooks/useNow';
import { useUnitOfBed } from '../hooks/useUnitOfBed';
import { getCleaningMissionQuery, getCleaningMissionQueryOptions } from '../queries';
import { describeBedLocation } from '../utils/bed-location';

type CleaningMissionDetailsPageProps = {
  missionId: string;
};

const CleaningMissionDetailsPage = ({ missionId }: CleaningMissionDetailsPageProps) => {
  const { t } = useTranslation('environmental-cleaning');
  const navigate = useNavigate();
  const { data: missions } = useSuspenseQuery(getCleaningMissionQueryOptions(missionId));
  useLiveQuery(getCleaningMissionQuery(missionId), missionId);
  const mission = missions[0];
  const now = useNow();
  const unitOfBed = useUnitOfBed();
  const location = mission && describeBedLocation(t, mission.bed, unitOfBed(mission.bed));

  const close = () => navigate({ to: '/environmental-cleaning/missions' });

  return (
    <Drawer
      anchor="bottom"
      open
      onClose={close}
      slotProps={{ backdrop: { sx: { bgcolor: 'rgba(8, 6, 50, 0.45)' } } }}
      PaperProps={{
        sx: {
          // La liste reste visible au-dessus : l'agent garde son repère.
          height: 'calc(100% - env(safe-area-inset-top) - 72px)',
          borderRadius: '24px 24px 0 0',
          boxShadow: '0 -8px 30px rgba(0, 0, 0, 0.2)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ flex: 'none', display: 'flex', justifyContent: 'center', paddingTop: '10px', paddingBottom: 1 }}>
        <Box sx={{ width: 40, height: 5, borderRadius: '3px', bgcolor: colors.grey[20] }} />
      </Box>
      <Stack
        direction="row"
        alignItems="center"
        gap={3}
        sx={{
          flex: 'none',
          paddingX: 4,
          paddingTop: 2,
          paddingBottom: '14px',
          borderBottom: `1px solid ${colors.grey[10]}`,
        }}
      >
        {mission && <CleaningMissionPrioritySquare priority={mission.priority} />}
        <Stack flex={1} minWidth={0} gap="2px">
          {mission && (
            <>
              <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
                {mission.bed.name}
              </Typography>
              {location && (
                <Typography
                  sx={{
                    fontSize: 14,
                    color: 'text.secondary',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {location}
                </Typography>
              )}
            </>
          )}
        </Stack>
        <IconButton
          aria-label={t('mission.close')}
          onClick={close}
          sx={{ flex: 'none', width: 44, height: 44, bgcolor: colors.blue[5], color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 4 }}>
        {mission ? (
          <CleaningMissionDetails mission={mission} now={now} />
        ) : (
          <Typography color="text.secondary">{t('mission.notFound')}</Typography>
        )}
      </Box>
      {mission && <CleaningMissionGestures mission={mission} onFinished={close} />}
    </Drawer>
  );
};

export default CleaningMissionDetailsPage;
